import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { createSafeCloseHandler } from '../../utils/modalUtils';
import { AddEscalationLevelModal } from './AddEscalationLevelModal';
import { createEscalationMatrixLevel } from '../../api/escalationMatrix';
import { ApiError } from '../../config/api';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddUser: (userId: string, userData: any) => Promise<void>;
  availableUsers: ProjectUserInterface[];
  projectUsers: ProjectUserInterface[];
  roles: any[];
  escalationLevels?: any[];
  projectId: string;
  onEscalationLevelsUpdate?: () => Promise<void>;
}

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  position: string;
  project_role_id: string;
  escalation_level_id: string;
  department: string;
  status: string;
}

// Invitation method types
type InvitationMethod = 'email' | 'sms' | 'phone';

import { normalizeAndValidatePhone } from '../../utils/phoneValidation';

export const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onAddUser,
  roles,
  escalationLevels = [],
  projectId,
  onEscalationLevelsUpdate
}) => {
  const { keycloak } = useKeycloak();
  const STORAGE_KEY = `addUserDraft-${projectId || 'global'}`;
  const initialFormData: UserFormData = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    project_role_id: '',
    escalation_level_id: '',
    department: '',
    status: 'Active'
  };
  
  // Use ref to persist form data across re-renders when escalation levels update
  const formDataRef = useRef<UserFormData>(initialFormData);
  
  const [formData, setFormData] = useState<UserFormData>(formDataRef.current);
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [selectedInvitationMethod, setSelectedInvitationMethod] = useState<InvitationMethod>('email');
  const [showAddEscalationLevelModal, setShowAddEscalationLevelModal] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  
  // Sync formData changes to ref
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  // Track first open to reset state, otherwise allow within-session draft
  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Fresh open: clear draft and reset form
      sessionStorage.removeItem(STORAGE_KEY);
      formDataRef.current = initialFormData;
      setFormData(initialFormData);
      setPhoneError('');
      setEmailError('');
    } else if (isOpen) {
      // Already open: restore draft if exists
      try {
        const draft = sessionStorage.getItem(STORAGE_KEY);
        if (draft) {
          const parsed = JSON.parse(draft) as UserFormData;
          setFormData(parsed);
          formDataRef.current = parsed;
        } else {
          setFormData(formDataRef.current);
        }
      } catch (e) {
        console.warn('AddUserModal: failed to parse draft', e);
        setFormData(formDataRef.current);
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, STORAGE_KEY]);

  // Persist draft while typing
  useEffect(() => {
    if (!isOpen) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch (e) {
      console.warn('AddUserModal: failed to save draft', e);
    }
  }, [formData, isOpen, STORAGE_KEY]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      // If nested Escalation Level modal is open, ignore Escape at parent level
      if (showAddEscalationLevelModal) return;
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose, showAddEscalationLevelModal]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'email') {
      setEmailError('');
    }

    // Real-time phone validation and normalization
    if (name === 'phone') {
      setPhoneError('');
      if (value) {
        const phoneValidation = normalizeAndValidatePhone(value);
        if (!phoneValidation.isValid) {
          setPhoneError(phoneValidation.error || 'Invalid phone number');
        } else {
          // Automatically update field with normalized number
          setFormData(prev => ({
            ...prev,
            phone: phoneValidation.normalizedPhone
          }));
          return; // Exit early to avoid setting value twice
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields (First Name, Last Name, Email, Phone)');
      return;
    }

    // Validate and normalize phone format
    const phoneValidation = normalizeAndValidatePhone(formData.phone);
    if (!phoneValidation.isValid) {
      const errorMsg = phoneValidation.error || 'Invalid phone number';
      toast.error(errorMsg);
      setPhoneError(errorMsg);
      return;
    }
    
    // Use normalized phone number and lowercase email
    const normalizedFormData = {
      ...formData,
      phone: phoneValidation.normalizedPhone,
      email: formData.email?.trim().toLowerCase()
    };

    setIsLoading(true);
    try {
      const apiData = {
        ...normalizedFormData
      };
      
      console.log('AddUserModal: Original form data:', formData);
      console.log('AddUserModal: Normalized form data:', normalizedFormData);
      console.log('AddUserModal: API data being sent:', apiData);
      console.log('AddUserModal: Phone validation result:', phoneValidation);
      
      // Use email as userId for the API call (since we're creating a new user)
      await onAddUser('', apiData);
      
      console.log('AddUserModal: user added successfully');
      
      // Reset form and ref
      const resetData = {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        position: '',
        project_role_id: '',
        escalation_level_id: '',
        department: '',
        status: 'Active'
      };
      setFormData(resetData);
      formDataRef.current = resetData;
      setPhoneError('');
      setEmailError('');
      
      // Close modal with slight delay to ensure all state updates complete
      setTimeout(() => {
        onClose();
        toast.success('User added successfully');
      }, 100);
    } catch (error) {
      console.error('AddUserModal: failed to add user', error);
      const parseErrorDetails = (err: unknown) => {
        const fieldErrors: Record<string, string> = {};
        let message = 'Failed to add user';

        if (err instanceof ApiError) {
          const responseBody = typeof err.response === 'string' ? (() => {
            try {
              return JSON.parse(err.response);
            } catch {
              return err.response;
            }
          })() : err.response;

          const details = Array.isArray((responseBody as any)?.detail) ? (responseBody as any).detail : [];

          if (details.length) {
            const messages: string[] = [];
            details.forEach((item: any) => {
              const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : undefined;
              const msg = item?.msg || item?.message || 'Validation error';
              const input = item?.input ? ` (input: ${item.input})` : '';

              if (field) {
                fieldErrors[field] = `${msg}${input}`;
              }

              messages.push(`${field ? `${field}: ` : ''}${msg}${input}`);
            });

            if (messages.length) {
              message = messages.join('\n');
            }
          } else {
            message = err.message || message;
          }
        } else if (err instanceof Error) {
          message = err.message;
        }

        return { fieldErrors, message };
      };

      const { fieldErrors, message } = parseErrorDetails(error);

      if (fieldErrors.phone) {
        setPhoneError(fieldErrors.phone);
        phoneInputRef.current?.focus();
      }

      if (fieldErrors.email) {
        setEmailError(fieldErrors.email);
        emailInputRef.current?.focus();
      }

      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvitationMethodChange = (method: InvitationMethod) => {
    if (method === 'email') {
      setSelectedInvitationMethod(method);
    } else {
      toast.info('This feature is in development');
    }
  };

  const handleEscalationLevelCreated = async (levelData: any) => {
    const token = keycloak.token;
    if (!token || !projectId) {
      toast.error('Authentication required');
      return;
    }

    try {
      // Create escalation level via API
      const newLevel = await createEscalationMatrixLevel(token, projectId, levelData);
      
      // Keep all current form data and preselect newly created level
      setFormData((prev) => {
        const next = { ...prev, escalation_level_id: newLevel.id };
        formDataRef.current = next;
        return next;
      });
      
      // Close the add-level modal only
      setShowAddEscalationLevelModal(false);
      toast.success('Escalation level created successfully');
      
      // Refresh levels list so dropdown shows the new level
      if (onEscalationLevelsUpdate) {
        await onEscalationLevelsUpdate();
      }
    } catch (error) {
      console.error('Failed to create escalation level:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create escalation level';
      toast.error(errorMessage);
      throw error; // Re-throw to let the modal handle it
    }
  };

  // Create safe close handler
  const handleSafeClose = createSafeCloseHandler(
    formData, 
    () => {
      sessionStorage.removeItem(STORAGE_KEY);
      onClose();
    }, 
    ['status'] // Exclude auto-filled fields
  );

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
      onClick={() => {
        // Don't close parent modal while the nested Add Escalation Level modal is open
        if (showAddEscalationLevelModal) return;
        handleSafeClose();
      }}
    >
      <button
        onClick={() => {
          if (showAddEscalationLevelModal) return;
          handleSafeClose();
        }}
        className="fixed top-4 right-4 z-[60] text-[#aaa] hover:text-[#555] p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div 
        className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[95vh] overflow-y-auto border border-[#eaeaea]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <h2 className="text-xl font-semibold">Add Team Member1</h2>
        </div>

        {/* Invitation Method Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#444] mb-2">
            Invitation Method
          </label>
          <div className="grid grid-cols-3 gap-2">
            {/* Email Option */}
            <div 
              onClick={() => handleInvitationMethodChange('email')}
              className={`relative p-3 border-2 rounded-lg cursor-pointer transition-all ${
                selectedInvitationMethod === 'email' 
                  ? 'border-[#888] bg-[#f5f5f5]' 
                  : 'border-[#eaeaea] hover:border-[#eaeaea]'
              }`}
            >
              <div className="flex flex-col items-center space-y-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                  selectedInvitationMethod === 'email' ? 'bg-[#555]' : 'bg-[#bbb]'
                }`}>
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className={`text-xs font-medium ${
                  selectedInvitationMethod === 'email' ? 'text-[#171717]' : 'text-[#444]'
                }`}>
                  Email
                </span>
              </div>
            </div>

            {/* SMS Option */}
            <div 
              onClick={() => handleInvitationMethodChange('sms')}
              className="relative p-3 border-2 border-[#eaeaea] rounded-lg cursor-pointer transition-all hover:border-[#eaeaea] opacity-60"
            >
              <div className="flex flex-col items-center space-y-1">
                <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#bbb]">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-[#444]">SMS</span>
              </div>
              <div className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] px-1 py-0.5 rounded-full">
                In dev
              </div>
            </div>

            {/* Phone Option */}
            <div 
              onClick={() => handleInvitationMethodChange('phone')}
              className="relative p-3 border-2 border-[#eaeaea] rounded-lg cursor-pointer transition-all hover:border-[#eaeaea] opacity-60"
            >
              <div className="flex flex-col items-center space-y-1">
                <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#bbb]">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <span className="text-xs font-medium text-[#444]">Phone</span>
              </div>
              <div className="absolute -top-1 -right-1 bg-orange-500 text-white text-[10px] px-1 py-0.5 rounded-full">
                In dev
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Required Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#444] mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              ref={emailInputRef}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                emailError
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-[#eaeaea] focus:ring-0'
              }`}
              required
            />
            {emailError && (
              <p className="mt-1 text-sm text-red-600 whitespace-pre-line">{emailError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#444] mb-1">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              ref={phoneInputRef}
              placeholder="6504415356 (will auto-format to +16504415356)"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 ${
                phoneError 
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                  : 'border-[#eaeaea] focus:ring-0'
              }`}
              required
            />
            {phoneError && (
              <p className="mt-1 text-sm text-red-600">{phoneError}</p>
            )}
          </div>

          {/* Optional Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Position
              </label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                placeholder="e.g. Project Manager, Engineer"
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Department
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Role
              </label>
              <select
                name="project_role_id"
                value={formData.project_role_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              >
                <option value="">Select a role</option>
                {(roles || []).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name?.replace('Agency', 'Company') || role.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#444] mb-1">
                Escalation Level
              </label>
              <select
                name="escalation_level_id"
                value={formData.escalation_level_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-0"
              >
                <option value="">Select a level</option>
                {(escalationLevels || []).map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-[#888]">
                {escalationLevels.length === 0 ? (
                  <>
                    No levels. <button 
                      type="button"
                      onClick={() => setShowAddEscalationLevelModal(true)}
                      className="text-[#171717] hover:text-[#333] underline"
                    >
                      Create one
                    </button>
                  </>
                ) : (
                  <>
                    To create a level click <button 
                      type="button"
                      onClick={() => setShowAddEscalationLevelModal(true)}
                      className="text-[#171717] hover:text-[#333] underline"
                    >
                      here
                    </button> and open "Add Escalation Level"
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#555] border border-[#eaeaea] rounded-lg hover:bg-[#fafafa]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.first_name || !formData.last_name || !formData.email || !formData.phone || phoneError.length > 0}
              className="px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#333] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Adding...' : 'Add User'}
            </button>
          </div>
        </form>
      </div>

      {/* Add Escalation Level Modal */}
      <AddEscalationLevelModal
        isOpen={showAddEscalationLevelModal}
        onClose={() => setShowAddEscalationLevelModal(false)}
        onAddLevel={handleEscalationLevelCreated}
        projectId={projectId}
      />
    </div>
  );
}; 